import { useAuthQuery } from '@/hooks/use-auth-query'
import { getBusinesses } from '@/api/partners'
import { processMidList } from '@/utils/mid-utils'

/**
 * Custom hook for fetching and managing partners data
 * Maps business data from the API to the format expected by the UI
 */
export const usePartners = () => {
  const {
    data: businessesResponse,
    isLoading,
    error,
    refetch,
  } = useAuthQuery({
    queryKey: ['partners'],
    queryFn: getBusinesses,
    refetchOnWindowFocus: false,
    staleTime: 30000,
    onError: error => {
      console.error('Failed to fetch partners:', error?.message)
    },
    onSuccess: () => {},
  })

  // Transform API data to match UI expectations
  const transformBusinessData = responseData => {
    // The API returns { businesses: [...] }, so we need to extract the businesses array
    const businesses = responseData?.businesses || responseData?.data?.businesses || responseData

    if (!businesses || !Array.isArray(businesses)) {
      return { groups: [], partners: [] }
    }

    const groups = []
    const partners = []
    const groupMap = new Map()

    businesses.forEach((business, businessIndex) => {
      // Create group entry
      const groupId = businessIndex + 1 // Use index as ID for now
      const group = {
        id: groupId,
        businessId: business.businessId, // Add businessId for API calls
        ledgerEntityId: business.ledgerEntityId || '', // Add ledgerEntityId from API
        name: business.businessName || 'Unnamed Business',
        status: 'Active', // Default status since API doesn't provide this
      }
      groups.push(group)
      groupMap.set(business.businessId, groupId)

      // Create partner entries for each business location
      if (business.locations && Array.isArray(business.locations)) {
        business.locations.forEach(location => {
          // Separate MIDs by type and remove prefixes
          const { spadeCounterPartyId, spadeLocationId, highnoteId } = processMidList(location.mids || [])

          const partner = {
            id: `${business.businessId}_${location.businessLocationId}`,
            name: location.businessLocationName || 'Unnamed Location',
            groupId: groupId,
            groupName: group.name,
            status: location.isActive ? 'Active' : 'Inactive',
            partnerType: location.businessLocationType || 'DEALERSHIP',
            street1: location.street1 || '',
            street2: location.street2 || '',
            city: location.city || '',
            state: location.state || '',
            zip: location.postalCode || '',
            spadeCounterPartyIds: spadeCounterPartyId ? spadeCounterPartyId.split(',') : [],
            spadeLocationIds: spadeLocationId ? spadeLocationId.split(',') : [],
            highnoteIds: highnoteId ? highnoteId.split(',') : [],
            rewardClasses: location.rewardClasses || [],
            lastUpdated: location.updatedAt,
            businessId: business.businessId,
            businessLocationId: location.businessLocationId,
          }
          partners.push(partner)
        })
      }
    })

    // Sort partners alphabetically by business name, then by business location name
    partners.sort((a, b) => {
      // First sort by business name (group name)
      const businessNameA = a.groupName.toLowerCase()
      const businessNameB = b.groupName.toLowerCase()

      if (businessNameA !== businessNameB) {
        return businessNameA.localeCompare(businessNameB)
      }

      // If business names are the same, sort by location name
      const locationNameA = a.name.toLowerCase()
      const locationNameB = b.name.toLowerCase()
      return locationNameA.localeCompare(locationNameB)
    })

    return { groups, partners }
  }

  const { groups, partners } = transformBusinessData(businessesResponse)

  return {
    groups,
    partners,
    isLoading,
    error,
    refetch,
  }
}

export default usePartners
